import { Progress, ProgressProps } from '@mantine/core';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { ColorsV2 } from 'src/core/themes/constants/colors';

export const PosAnimatingProgressBar = ({
  enableGlow,
  isError,
  ...props
}: { enableGlow?: boolean; isError?: boolean } & ProgressProps) => {
  const [animatedValue, setAnimatedValue] = useState(0);
  const { isDarkMode } = useSiteTheme();

  useEffect(() => {
    // Animate from 0 to the target value on mount
    // Using setTimeout to ensure the transition happens after initial render
    const timer = setTimeout(() => {
      setAnimatedValue(props.value ?? 0);
    }, 50);

    return () => clearTimeout(timer);
  }, [props.value]);

  const cssStyles = ((props.style as CSSProperties) ?? {}) as CSSProperties;

  // Dark mode: #5721B7 to #D668CD (layer blur 20)
  // Light mode: use pink/purple tones
  const blurColors = useMemo(
    () =>
      isError
        ? isDarkMode
          ? {
              color1: 'rgba(159, 43, 24, 0.6)',
              color2: 'rgba(252, 114, 95, 0.4)',
              color3: 'rgba(159, 43, 24, 0.5)',
            }
          : {
              color1: 'rgba(252, 114, 95, 0.15)',
              color2: 'rgba(252, 114, 95, 0.1)',
              color3: 'rgba(159, 43, 24, 0.125)',
            }
        : isDarkMode
          ? {
              color1: 'rgba(87, 33, 183, 0.6)',
              color2: 'rgba(214, 104, 205, 0.4)',
              color3: 'rgba(87, 33, 183, 0.5)',
            }
          : {
              color1: 'rgba(214, 104, 205, 0.15)',
              color2: 'rgba(214, 104, 205, 0.1)',
              color3: 'rgba(103, 29, 231, 0.125)',
            },
    [isDarkMode, isError]
  );

  // Only apply -5px offset if there's enough space (animatedValue * 0.3 > 5px equivalent)
  // Approximate: if animatedValue > 15%, there's enough space for the offset
  const blurOffset = animatedValue > 15 ? -5 : 0;
  const blurWidthOffset = blurOffset < 0 ? -2 : -8;

  return (
    <div
      style={{
        position: 'relative',
        width: cssStyles.width,
        height: cssStyles.height,
        padding: '10px 0', // Add padding to give blur space
      }}
    >
      {/* Left-extended blur layer - wider coverage on the left side */}
      {enableGlow && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: `${animatedValue * 0.45}%`,
            width: `calc(${animatedValue * 0.55}% + ${blurWidthOffset}px)`,
            height: 0,
            background: 'transparent',
            transform: 'translateY(-50%)',
            boxShadow: `
            ${blurOffset}px 0 10px 2px ${blurColors.color1},
            ${blurOffset}px 0 10px 2px ${blurColors.color2},
            ${blurOffset}px 0 10px 8px ${blurColors.color3}
          `,
            transition: 'left 600ms linear, width 600ms linear',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}

      {/* Main progress bar with gradient fill */}
      <Progress
        {...props}
        value={animatedValue}
        color={vars.color.surfaceBrand}
        style={{ ...props.style, position: 'relative', zIndex: 1 }}
        styles={{
          root: {
            transition: 'all 600ms linear',
            backgroundColor:
              cssStyles.backgroundColor ?? vars.color.surfaceSecondary,
          },
          section: {
            background: `linear-gradient(to right, ${
              isError ? '#9F2B18' : '#671DE7'
            }, 
                ${isError ? '#F5C9C2' : '#D4B9FF'})`,
            transition: 'width 600ms linear',
            borderRadius: cssStyles.borderRadius ?? '8px',
          },
        }}
      />
    </div>
  );
};
